import { Trans } from '@lingui/react/macro';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components';
import { Button } from '@repo/videa-components/src/ui/button';
import { format } from 'date-fns';
import { useAtomValue } from 'jotai';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import type { AdjustmentVersion } from '../../../../react-query/useGetSharpenedImages';
import { usePreferenceHistory } from '../../../../react-query/usePreferenceHistory';
import {
  usePreferences,
  usePreferencesPracticeUpdate,
} from '../../../../react-query/usePreferences';

import {
  finalFilterAtom,
  selectedAdjustmentTypeAtom,
  selectedImageIdAtom,
  selectedPatientIdAtom,
  selectedVisitAtom,
} from './atoms';
import { HD_SLOT_CONFIG, adjustmentVersionLabel } from './helpers';

export interface FilterSelectionModalFooterProps {
  onCancel: () => void;
}

export const FilterSelectionModalFooter = ({
  onCancel,
}: FilterSelectionModalFooterProps) => {
  const selectedVisit = useAtomValue(selectedVisitAtom);
  const selectedPatientId = useAtomValue(selectedPatientIdAtom);
  const selectedImageId = useAtomValue(selectedImageIdAtom);
  const finalFilter = useAtomValue(finalFilterAtom);
  const selectedAdjustmentType = useAtomValue(selectedAdjustmentTypeAtom);

  const { data: preferences } = usePreferences();

  const preferenceHistory = usePreferenceHistory({
    preferenceKey:
      HD_SLOT_CONFIG.find((slot) => slot.label === selectedAdjustmentType)
        ?.preferenceKey ?? '',
  });

  const preferencesUpdate = usePreferencesPracticeUpdate();
  const { trackEvent } = useTrackEvent();

  const lastUpdatedDate = preferenceHistory.data?.updatedAt
    ? format(new Date(preferenceHistory.data.updatedAt), 'MM/dd/yyyy')
    : undefined;
  const lastUpdatedBy =
    preferenceHistory.data?.user &&
    `${preferenceHistory.data.user.firstName} ${preferenceHistory.data.user.lastName}`;

  const isSaveDisabled =
    !selectedImageId ||
    finalFilter === null ||
    (finalFilter === preferences?.adjustmentVersion &&
      selectedAdjustmentType === 'HD1') ||
    (finalFilter === preferences?.adjustmentVersionHD2 &&
      selectedAdjustmentType === 'HD2') ||
    (finalFilter === preferences?.panoAdjustmentVersion &&
      selectedAdjustmentType === 'Pano');

  const selectedHDVersion = selectedAdjustmentType;
  const saveButtonLabel =
    preferences?.enableSharpeningHd2Selection ||
    preferences?.enablePanoAdjustmentSelection
      ? selectedHDVersion
      : 'HD';

  const showPreferenceSummary =
    selectedImageId && selectedVisit && selectedPatientId;

  const preferenceVersionLabel = adjustmentVersionLabel(
    (preferenceHistory.data?.value ?? null) as AdjustmentVersion,
  ).label;

  const handleSaveFilter = () => {
    if (selectedAdjustmentType === 'HD1' && finalFilter !== null) {
      preferencesUpdate.mutate(
        { adjustmentVersion: finalFilter },
        {
          onSuccess: () => {
            trackEvent(mixpanelEvents.sharpeningFilterSelection, {
              filter: finalFilter,
              version: 'HD1',
            });
          },
        },
      );
    } else if (selectedAdjustmentType === 'HD2' && finalFilter !== null) {
      preferencesUpdate.mutate(
        { adjustmentVersionHD2: finalFilter },
        {
          onSuccess: () => {
            trackEvent(mixpanelEvents.sharpeningFilterSelection, {
              filter: finalFilter,
              version: 'HD2',
            });
          },
        },
      );
    } else if (selectedAdjustmentType === 'Pano' && finalFilter !== null) {
      preferencesUpdate.mutate(
        { panoAdjustmentVersion: finalFilter },
        {
          onSuccess: () => {
            trackEvent(mixpanelEvents.sharpeningFilterSelection, {
              filter: finalFilter,
              version: 'Pano',
            });
          },
        },
      );
    }
  };

  return (
    <>
      <span className="vai-flex vai-text-sm vai-gap-6 vai-border-[1px] vai-border-zinc-200 vai-bg-zinc-50 vai-text-zinc-500 vai-p-2 vai-rounded-md">
        {!!showPreferenceSummary && (
          <>
            <span>
              <Trans>Current</Trans>
              <span> {selectedHDVersion} </span>
              <Trans>Profile: </Trans>
              <span>{preferenceVersionLabel}</span>
            </span>
            <span>
              {lastUpdatedDate && (
                <>
                  <Trans>Last updated: </Trans>
                  <span>{lastUpdatedDate}</span>
                </>
              )}
              {lastUpdatedBy && (
                <>
                  <Trans> by </Trans>
                  <span>{lastUpdatedBy}</span>
                </>
              )}
            </span>
          </>
        )}
        {!selectedImageId && (
          <div className="vai-text-right vai-text-gray-600">
            <Trans>Select a visit and image to continue</Trans>
          </div>
        )}
      </span>

      <div className="vai-flex vai-gap-3 vai-pt-4 vai-justify-end">
        <div className="vai-flex vai-gap-3">
          <Button variant="outline" onClick={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button onClick={handleSaveFilter} disabled={isSaveDisabled}>
                  <span className="vai-flex vai-items-center vai-gap-1">
                    <Trans>Save </Trans>
                    {saveButtonLabel}
                    <Trans> Profile</Trans>
                  </span>
                </Button>
              </div>
            </TooltipTrigger>
            {isSaveDisabled && !!selectedImageId && (
              <TooltipContent side="top" className="vai-max-w-48">
                {finalFilter === null ? (
                  <Trans>No filter is selected</Trans>
                ) : (
                  <Trans>
                    Selected filter is the same as the current setting
                  </Trans>
                )}
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </div>
    </>
  );
};
